import React, { memo, useContext } from 'react'
import moment from 'moment'
import CheckInIcon from 'transfix-icons/build/CheckInIcon'
import CheckOutIcon from 'transfix-icons/build/CheckOutIcon'
import { UserContext } from 'apps/fleet-planner/context'
import { IStop, SETTINGS_TIME_FORMAT } from 'apps/fleet-planner/types'
import {
  IActivity,
  isActivityCheckInTimeUpdated,
  isActivityCheckOutTimeUpdated,
  isActivityStatusUpdated,
} from 'apps/fleet-planner/types/activity'
import { currentTimezone } from 'constants/AllowedTimezones'
import FORMATS from 'constants/datetime'
import { getCurrentStopName } from './ActivityText'

interface IActivitySubtextProps {
  activity: IActivity
  loadStops?: IStop[]
  className?: string
  iconSize?: 'small' | 'large'
}
interface IActivitySubtextIconProps {
  activity: IActivity
  size?: 'small' | 'large'
}

export const DATETIME_UNKNOWN = 'at date unknown, time unknown'

export const ActivitySubtextTextItem: React.FC<{
  children: React.ReactNode
  className: string
}> = ({ children, className = '' }) => (
  <div className={`${className} text-success body-semibold flex`}>{children}</div>
)

const Subtext = memo<IActivitySubtextProps>(({ activity, loadStops = [] }) => {
  const { timeFormat } = useContext(UserContext)
  const currentTimeFormat =
    timeFormat === SETTINGS_TIME_FORMAT.STANDARD ? FORMATS.datetimeShort12H : FORMATS.datetimeShort

  const getSubtext = () => {
    /**
     * Returns a arrival/departure string,
     * e.g 'D1: Arrived 10/20 3:30pm CDT'
     */
    if (isActivityCheckInTimeUpdated(activity) || isActivityCheckOutTimeUpdated(activity)) {
      const { metadata } = activity || {}
      const { stop } = metadata || {}
      const stopName = getCurrentStopName(stop?.id, loadStops)
      const isRemoval =
        (isActivityCheckInTimeUpdated(activity) && !activity.metadata.stop?.checkedInAt) ||
        (isActivityCheckOutTimeUpdated(activity) && !activity.metadata.stop?.checkedOutAt)
      const stopAction = isActivityCheckInTimeUpdated(activity) ? 'Arrived' : 'Departed'

      const stopTime = isActivityCheckInTimeUpdated(activity)
        ? activity.metadata.stop?.checkedInAt
        : activity.metadata.stop?.checkedOutAt
      const currentStop = loadStops.find(s => s.id === stop?.id)
      const formattedDateTime = moment
        .tz(stopTime, currentStop?.displayTimezone || currentTimezone)
        .format(currentTimeFormat)
      const stopDateTime = isRemoval ? DATETIME_UNKNOWN : formattedDateTime

      return `${stopName}: ${stopAction} ${stopDateTime}`
    }

    if (isActivityStatusUpdated(activity)) {
      const { metadata } = activity || {}
      const { stop } = metadata || {}
      const stopName = getCurrentStopName(stop?.id, loadStops)
      const isArrival = stop?.checkedInAt !== undefined
      const stopAction = isArrival ? 'Arrived' : 'Departed'
      const stopTime = stop?.checkedInAt || stop?.checkedOutAt
      const currentStop = loadStops.find(s => s.id === stop?.id)
      const formattedDateTime = moment
        .tz(stopTime, currentStop?.displayTimezone || currentTimezone)
        .format(currentTimeFormat)

      return `${stopName}: ${stopAction} ${formattedDateTime}`
    }

    return ''
  }

  return <span className='pl2'>{getSubtext()}</span>
})

const SubtextIcon = memo<IActivitySubtextIconProps>(({ activity, size = 'large' }) => {
  if (isActivityCheckInTimeUpdated(activity)) {
    return <CheckInIcon size={size} color='coolGray4' />
  }
  if (isActivityCheckOutTimeUpdated(activity)) {
    return <CheckOutIcon size={size} color='coolGray4' />
  }
  if (isActivityStatusUpdated(activity)) {
    const { metadata } = activity || {}
    const { stop } = metadata || {}
    const IconComponent = stop?.checkedInAt ? CheckInIcon : CheckOutIcon
    return <IconComponent size={size} color='coolGray4' />
  }
  return <></>
})

export const ActivitySubtext = memo<IActivitySubtextProps>(
  ({
    activity,
    loadStops = [],
    className = 'load-details-activity-text-item',
    iconSize = 'large',
  }) => {
    return (
      <ActivitySubtextTextItem className={className}>
        <SubtextIcon activity={activity} size={iconSize} />
        <Subtext activity={activity} loadStops={loadStops} />
      </ActivitySubtextTextItem>
    )
  }
)
